import { type ReactNode, type ButtonHTMLAttributes } from "react";
import Icon from "./Icon";

type ButtonVariant = "primary" | "secondary" | "ghost" | "cta-lime" | "cta-pink" | "cta";
type ButtonSize = "sm" | "md" | "lg" | "inline";
type ButtonTextStyle = "legacy" | "v2";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    /** Text style: "legacy" = uppercase italic black, "v2" = bold display font */
    textStyle?: ButtonTextStyle;
    /** SVG icon name (kebab-case) to show before label */
    icon?: string;
    /** SVG icon name (kebab-case) to show after label */
    iconRight?: string;
    /** Full width */
    fullWidth?: boolean;
    /** Loading state */
    loading?: boolean;
    children: ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
    primary:
        "bg-accent-lime text-background-dark cta-neon-lime active:scale-[0.98] shadow-[0_0_8px_rgba(204,255,0,0.4)]",
    secondary:
        "bg-white/5 hover:bg-white/10 text-white/80 border border-white/10 transition-colors",
    ghost:
        "bg-transparent hover:bg-white/5 text-white/60 hover:text-white transition-colors",
    "cta-lime":
        "bg-accent-lime text-background-dark shadow-[0_10px_30px_-10px_rgba(204,255,0,0.5)] active:scale-[0.98]",
    "cta-pink":
        "bg-accent-pink text-background-dark cta-neon-pink active:scale-[0.98] shadow-[0_0_8px_rgba(255,0,255,0.4)]",
    cta:
        "bg-white text-[#111012] active:scale-[0.98] active:bg-white/90",
};

const sizeClasses: Record<ButtonSize, string> = {
    sm: "px-4 py-1.5 text-[10px] rounded-full",
    md: "px-6 py-3 text-sm rounded-full",
    lg: "h-[clamp(52px,6.9dvh,64px)] text-[20px] leading-[28px] rounded-full",
    /** Mid-screen CTA: smaller than full-width bottom CTAs */
    inline: "h-[clamp(44px,5.5dvh,52px)] px-[32px] text-[16px] rounded-full",
};

const iconSizes: Record<ButtonSize, number> = {
    sm: 14,
    md: 18,
    lg: 24,
    inline: 20,
};

const textStyleClasses: Record<ButtonTextStyle, string> = {
    legacy: "font-black uppercase italic tracking-tight",
    v2: "font-bold tracking-[-0.02em]",
};

export default function Button({
    variant = "primary",
    size = "sm",
    textStyle = "legacy",
    icon,
    iconRight,
    fullWidth = false,
    loading = false,
    children,
    className = "",
    disabled,
    style,
    ...props
}: ButtonProps): ReactNode {
    return (
        <button
            className={`
        ${textStyleClasses[textStyle]} transition-all
        flex items-center justify-center gap-2
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${fullWidth ? "w-full" : ""}
        ${disabled || loading ? "opacity-50 cursor-not-allowed" : ""}
        ${className}
      `}
            style={textStyle === "v2" ? { fontFamily: "var(--font-display)", ...style } : style}
            disabled={disabled || loading}
            {...props}
        >
            {loading ? (
                <Icon name="spinner" size={iconSizes[size]} spinning inline />
            ) : (
                <>
                    {icon && <Icon name={icon} size={iconSizes[size]} inline />}
                    {children}
                    {iconRight && (
                        <span className="group-hover:translate-x-1 transition-transform inline-flex">
                            <Icon name={iconRight} size={iconSizes[size]} inline />
                        </span>
                    )}
                </>
            )}
        </button>
    );
}
